import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import NewsCard from "@/components/base/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { works } from "@/mocks/works";
import { newsList, newsTypeLabel } from "@/mocks/news";
import { quickKeywords } from "@/mocks/home";
import { workTypeLabel } from "@/utils/labels";

type TabKey = "all" | "works" | "news";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部结果" },
  { key: "works", label: "影视作品" },
  { key: "news", label: "动态资讯" },
];

const hotGenres = ["悬疑", "剧情", "访谈", "爱情", "电影", "电视剧"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState<TabKey>("all");

  usePageMeta({
    title: query ? `搜索「${query}」－翔田千里影视作品与动态资讯` : "全站搜索－翔田千里影视作品与动态资讯",
    description:
      "在翔田千里影视资料站内检索影视作品与动态资讯，支持按作品名、类型标签、角色、导演与资讯标题、摘要、标签搜索，结果按作品与资讯分类展示。",
    keywords: "翔田千里搜索,作品检索,资讯检索,影视作品查找",
  });

  useEffect(() => {
    setKeyword(query);
    setTab("all");
  }, [query]);

  const normalized = query.toLowerCase();

  const workResults = useMemo(() => {
    if (!normalized) return [];
    return works.filter((work) => {
      const haystack = [
        work.title,
        work.enTitle,
        work.genre.join(" "),
        work.role,
        work.roleType,
        work.director,
        String(work.year),
        workTypeLabel(work.type),
        work.synopsis,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [normalized]);

  const newsResults = useMemo(() => {
    if (!normalized) return [];
    return newsList.filter((item) => {
      const haystack = [
        item.title,
        item.summary,
        item.tags.join(" "),
        newsTypeLabel(item.type),
        item.date,
        item.content.map((block) => [...block.paragraphs, block.heading || ""].join(" ")).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [normalized]);

  const totalCount = workResults.length + newsResults.length;
  const showWorks = tab === "all" || tab === "works";
  const showNews = tab === "all" || tab === "news";

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const runSearch = (value: string) => {
    setSearchParams({ q: value });
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200/70 bg-background-50">
        <div className="container-page py-9 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "全站搜索" }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="font-heading text-3xl leading-tight text-foreground-950 md:text-[40px]">
              全站搜索
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              同时检索影视作品与动态资讯。支持作品名、英文名、类型标签、角色名、导演，以及资讯标题、摘要与标签。
            </p>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-3 rounded-md border border-background-300/70 bg-background-100 px-4 py-3 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入作品名、类型或关键字"
                  aria-label="搜索关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                搜索
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-400">试试：</span>
              {hotGenres.map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => runSearch(genre)}
                  className="cursor-pointer rounded-full border border-background-200 bg-background-100 px-3 py-1 text-[11px] text-foreground-700 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-800"
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-8 md:py-10">
        <div className="container-page">
          {query ? (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-foreground-600">
                  关键词「<span className="text-foreground-950">{query}</span>」共找到{" "}
                  <span className="font-medium text-accent-700">{totalCount}</span> 条结果
                </p>
                <div className="flex flex-wrap gap-2">
                  {tabs.map((item) => {
                    const count =
                      item.key === "works"
                        ? workResults.length
                        : item.key === "news"
                          ? newsResults.length
                          : totalCount;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setTab(item.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                          tab === item.key
                            ? "bg-primary-600 text-background-50"
                            : "border border-background-300/70 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {item.label}
                        <span className={`ml-1.5 ${tab === item.key ? "text-background-50/70" : "text-foreground-400"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {totalCount === 0 && (
                <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                    <i className="ri-search-eye-line" aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg text-foreground-900">没有找到匹配的内容</h2>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-foreground-500">
                    试试更短的关键词，例如「夜行」「访谈」「悬疑」，或者浏览全部作品与资讯列表。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <Link
                      to="/works"
                      className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-xs text-background-50 hover:bg-primary-700"
                    >
                      <i className="ri-film-line" aria-hidden="true"></i>
                      浏览全部作品
                    </Link>
                    <Link
                      to="/news"
                      className="inline-flex items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 hover:border-primary-300"
                    >
                      <i className="ri-newspaper-line" aria-hidden="true"></i>
                      浏览全部资讯
                    </Link>
                  </div>
                </div>
              )}

              {showWorks && workResults.length > 0 && (
                <div className="mt-7">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg text-foreground-950">影视作品</h2>
                    <span className="text-[11px] text-foreground-400">{workResults.length} 部</span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                    {workResults.slice(0, tab === "all" ? 3 : 12).map((work) => (
                      <WorkCard key={work.id} work={work} />
                    ))}
                  </div>
                  {tab === "all" && workResults.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setTab("works")}
                      className="mt-4 inline-flex cursor-pointer items-center gap-1.5 text-xs text-primary-700 hover:text-accent-700"
                    >
                      查看全部 {workResults.length} 部作品
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
              )}

              {showNews && newsResults.length > 0 && (
                <div className="mt-7">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg text-foreground-950">动态资讯</h2>
                    <span className="text-[11px] text-foreground-400">{newsResults.length} 条</span>
                  </div>
                  <div className="mt-4 space-y-3">
                    {newsResults.slice(0, tab === "all" ? 3 : 12).map((item) => (
                      <NewsCard key={item.id} item={item} horizontal />
                    ))}
                  </div>
                  {tab === "all" && newsResults.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setTab("news")}
                      className="mt-4 inline-flex cursor-pointer items-center gap-1.5 text-xs text-primary-700 hover:text-accent-700"
                    >
                      查看全部 {newsResults.length} 条资讯
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
                <h2 className="font-heading text-xl text-foreground-950">从哪里开始？</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  输入关键词开始检索，或直接点击下列热门内容。搜索范围包含作品资料与全部资讯正文。
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {quickKeywords.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => runSearch(item)}
                      className="cursor-pointer rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-800"
                    >
                      <i className="ri-search-line mr-1.5 text-foreground-400" aria-hidden="true"></i>
                      {item}
                    </button>
                  ))}
                </div>

                <h2 className="mt-8 font-heading text-lg text-foreground-950">按类型浏览</h2>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {["电影作品", "电视剧作品", "访谈", "活动报道", "悬疑题材", "最新动态"].map((item, index) => (
                    <Link
                      key={item}
                      to={
                        index === 0
                          ? "/works?type=movie"
                          : index === 1
                            ? "/works?type=series"
                            : index === 2
                              ? "/news?type=interview"
                              : index === 3
                                ? "/news?type=event"
                                : index === 4
                                  ? "/search?q=悬疑"
                                  : "/news"
                      }
                      className="flex items-center justify-between rounded-md border border-background-200 bg-background-100 px-3 py-3 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-50"
                    >
                      {item}
                      <i className="ri-arrow-right-up-line text-foreground-400" aria-hidden="true"></i>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">最新收录</h2>
                <div className="mt-3 space-y-3">
                  {newsList.slice(0, 3).map((item) => (
                    <Link key={item.id} to={`/news/detail/${item.id}`} className="group block">
                      <p className="clamp-2 text-xs leading-relaxed text-foreground-800 group-hover:text-primary-700">
                        {item.title}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-400">{item.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}